/**
 * INSERT (`type: "Block"`) render lift — the ONE seam both render branches
 * of `SceneBulkLoad` go through to turn a typed `BlockInsertEntity` into
 * drawable geometry.
 *
 * # Why this module exists (fix: cache-direct never rendered INSERTs)
 *
 * The typed flatten `blockFromEntity` (`adapters/typed/blockFromEntity.ts`)
 * turns ONE INSERT into MANY renderables (`${insertId}::${child.id}-${i}`),
 * which never fit the one-entity → one-renderable contract of the
 * cache-direct lift (`state/spatialBuilder.ts liftRenderableForId`). So the
 * flatten was called ONLY on the legacy (small-drawing) branch, and every
 * streamed (>= 2 MB) import — whose `spaces[].entities` stay empty and
 * whose render therefore runs cache-direct — silently drew zero INSERTs:
 * the lift returned `null` for the `Block` kind, and the per-frame zoom
 * re-lift (`FrameLoops` Pass 2) uses the same lift, so zooming in never
 * recovered them either.
 *
 * # Design — reuse the MLEADER block-content satellite mechanism
 *
 * The MLEADER block-content path already solves "one spatial-index entity →
 * many render-only satellites" on the cache-direct branch: the parent is
 * the single spatial-index / pickable entity, and its flattened children
 * ride with it as RENDER-ONLY satellites (absent from the spatial index and
 * from `liftedEntityIds`; added / removed in lockstep with the parent by
 * `populateLiftedAndBlockChildren` + `FrameLoops` Passes 1/2 through the
 * shared collector `collectRenderSatellites` in `./renderSatellites.ts`).
 *
 * {@link liftBlockInsertRenderable} builds exactly that shape for an
 * INSERT: a single `type: "Block"` WRAPPER renderable whose geometry is the
 * pre-existing `"block-insert"` kind (`types.ts`) with the flattened
 * children in `childRenderables`. The wrapper itself contributes NO line
 * vertices (`batchedLayer.ts` `flattenForBucket` returns `[]` for
 * `block-insert`), so the children — each a first-class per-kind
 * renderable — are what the bucket / fill / text emitters draw, exactly as
 * the legacy branch's flat push does.
 *
 * {@link flattenBlockInsertForRender} is the single render call site of
 * `blockFromEntity`: the legacy branch pushes its result flat into the
 * renderable feed (ids / geometry byte-identical to before), and the
 * cache-direct wrapper wraps the SAME result — so the two branches can never
 * drift (pinned by the "legacy ↔ cache-direct parity" block of
 * `__tests__/liftBlockInsert.test.ts`).
 *
 * # Child layer / colour — deliberately NOT the MLEADER rule
 *
 * Unlike an MLEADER (a SINGLE object on its OWN layer, whose block content
 * is re-homed onto the parent layer by the collector), an INSERT's children
 * keep the layer `blockFromEntity` already resolved: only layer-"0" children
 * follow the INSERT's layer (`resolveBlockChildLayer`), every other child
 * stays on its authored layer and is hidden / shown by THAT layer — the same
 * result the legacy branch's flat push produces. Colour / linetype are baked
 * per child by the flatten (BYBLOCK → the INSERT's own resolved colour;
 * BYLAYER → the child's resolved layer colour).
 *
 * # Parent-layer conjunction (Codex PR #784 round 3 P1)
 *
 * AutoCAD hides the WHOLE block reference when the INSERT's OWN layer is off
 * / frozen (globally and per viewport, VPLAYER) regardless of the children's
 * layers, while each child is STILL governed by its own layer. So every
 * child returned by {@link flattenBlockInsertForRender} is stamped with
 * `renderParentLayers` = EVERY enclosing reference layer, outermost first
 * (`[insert.layer]` for a direct child; `[insert.layer, …nested layers]` for a
 * child inside nested INSERTs — round 3 P1 #4) — a shallow copy per child, the
 * flatten's output is never mutated — on BOTH branches, and draws only when
 * neither its own layer nor ANY enclosing reference's layer is hidden. The
 * nested part of the chain is collected out-of-band by the flatten
 * (`BlockChildAncestry`, `adapters/typed/blockFromEntity.ts`) precisely so its
 * parity-pinned renderables stay byte-identical. The conjunction is
 * implemented once in `../layerChainVisibility.ts` and applied on the paths
 * that draw or pick a FLATTENED child: the batched-line bucket key
 * (`batchedLayer.ts` — a child on B under an INSERT on A gets its own
 * `(B, parents=[A])` bucket, so hiding A is a per-bucket toggle, never a
 * per-slot rewrite), the global layer effect + FrameLoops relift handle path
 * (`state/layerVisibility.ts`), the direct-mesh composite
 * (`./entityRenderVisibility.ts`), the per-VP mask
 * (`rendering/viewportRenderPass.ts`), the hit-test / drag-select filters and
 * the grips locked gate. The legacy (< 2 MB) flat push gets the identical
 * rule for free through the same stamp — its children are first-class
 * spatial-index entries WITHOUT `renderParentId`, but WITH the chain.
 *
 * Residual (documented, NOT a parity claim — a follow-up tracked in the ACDb
 * comparison audit,
 * `docs/architecture/2026-09-05_acdb-comparison-and-delivery-audit.md` §11,
 * branch `claude/core-storage-acdb-comparison-5mrjol`): the INSTANCED block
 * path (`adapters/blockInsertInstanced.ts` — repeated blocks drawn as one
 * `InstancedMesh`, per-instance visibility keyed on `userData.instanceLayers`
 * = the INSERT's layer only) never hides an instance for a child's own
 * explicit layer. (The round-2 residual "the stamp carries the TOP-LEVEL
 * INSERT's layer only" is CLOSED — see the paragraph above.)
 *
 * On the cache-direct branch each child additionally gains `renderParentId =
 * insert.id` so a child that renders as a DIRECT mesh (TEXT / HATCH /
 * WIPEOUT) stamps the PARENT's id for the per-viewport visibility mask,
 * which only keeps ids the spatial index knows about. The child's OWN layer
 * still governs it (Codex PR #784 P1): a direct mesh also stamps
 * `userData.entityLayer = child.layer` (+ `parentLayers`), which the VP mask
 * checks against the viewport's frozen layers (`viewportRenderPass.ts
 * isDirectMeshOnFrozenLayer`), and the satellites' layers are projected
 * into the VP `visibleLayers` / model-paper census from the parent →
 * satellites map (`./satelliteLayerProjection.ts`) so a child's batched
 * bucket on a layer with no top-level entity is neither culled as "absent"
 * nor classified "paper-only" — identical to the legacy branch, where each
 * child is its own spatial-index entry.
 *
 * # Scale — only VISIBLE INSERTs are lifted (Codex PR #784 round-8 P2)
 *
 * Each lift recursively materialises the reference's whole child tree, so the
 * cost is paid per lifted reference. Block stubs used to be EXEMPT from the
 * cache-direct LOD pre-classify drop (`isAlwaysLiftedStubType`,
 * lod/lodPolicy.ts) because a dropped stub could never be re-lifted — which
 * meant the bulk load flattened every reference in the drawing, sub-pixel or
 * not: 20 000 INSERTs of a 50-child definition expanded 1 000 000 children in
 * ~2.2 s before the first paint
 * (`__tests__/cacheDirectInsertLodScale.test.ts`).
 *
 * The exemption is gone. A sub-pixel-at-fit-to-extents stub is dropped and
 * recovered on demand — the spatial resolver falls back to its metadata stub
 * (`state/spatialBuilder.ts createMetadataStubResolver`) and `FrameLoops`
 * Pass 2's lift publishes the renderable and emits its direct meshes
 * (`scene/sceneBulkLoadHelpers.ts emitReliftDirectMeshes`) — so the same
 * synthetic now materialises 0 wrappers / 0 children at bulk load (~8 ms) and
 * one reference's 50 children in ~7 ms the frame it becomes visible.
 *
 * Measured cost for references that ARE visible at fit-to-extents: on
 * abi1-test.dxf, same process, same fixture, exemption ON vs OFF — 167 → 157
 * renderables, 2327 → 2199 satellites, 128.2 ms → 72.8 ms of lift; the 10
 * sub-pixel INSERTs it now drops carry those 128 satellites and recover on
 * demand in ~6.7 ms total
 * (`__tests__/liftBlockInsert.abi1.regression.test.ts`); on the П35Р 685k
 * drawing 28 INSERTs / 1042 children (audit §10–11). The legacy (< 2 MB)
 * branch flattens every INSERT at bulk-load, so this is the same cost class
 * for the visible set, and strictly less for the rest.
 */

import type {
  BlockDefinition,
  BlockInsertEntity,
} from "@dxf-ai-editor/drawing-model";
import { unionBounds } from "@dxf-ai-editor/cad-core";

import {
  entityForegroundHex,
  type AdapterContext,
} from "../adapters/adapterContext";
import {
  blockFromEntity,
  resolveBlockInsertTransform,
  resolveInsertLinetypeTyped,
  type BlockChildAncestry,
} from "../adapters/typed/blockFromEntity";
import { applyOffset } from "../coordinateOrigin";
import { resolveColorHexFromEntity } from "../rendering/colorResolution";
import type { RenderableEntity } from "../types";

/**
 * The single render-path call site of `blockFromEntity`.
 *
 * Returns the INSERT's flattened children in offset-world coordinates with
 * ids `${insert.id}::${child.id}-${i}` (nested INSERTs flatten recursively
 * into the same flat list), each stamped with `renderParentLayers =
 * [insert.layer, …every enclosing NESTED reference's layer]` (module doc
 * "Parent-layer conjunction") — the flatten's own output is parity-pinned
 * against the leaf adapters and is never mutated, so the stamp is a shallow
 * copy per child and the nested part arrives out-of-band through the
 * `BlockChildAncestry` sink. Empty when the INSERT
 * is annotatively hidden at the active scale, references an unknown block,
 * or the block has no drawable children — the documented "nothing to draw"
 * cases.
 *
 * `blocks` is passed explicitly (not read from `ctx.blocks`) so the legacy
 * branch keeps handing its `inputs.blockDefinitions` table through
 * unchanged; the cache-direct lift passes `ctx.blocks` (the same table,
 * threaded by `buildAdapterCtxBase`).
 */
export function flattenBlockInsertForRender(
  typedInsert: BlockInsertEntity,
  blocks: Record<string, BlockDefinition>,
  ctx: AdapterContext,
): RenderableEntity[] {
  // Codex PR #784 round 3 P1 — collect the per-child NESTED-reference ancestry
  // OUT-OF-BAND (`BlockChildAncestry`) so the flatten's returned objects stay
  // byte-identical (they are parity-pinned against the leaf adapters), while
  // the FULL chain is stamped here, on this module's own shallow copies.
  const ancestry: BlockChildAncestry = new Map();
  const flattened = blockFromEntity(
    typedInsert,
    blocks,
    ctx,
    undefined,
    undefined,
    ancestry,
  );
  const insertLayer = typedInsert.layer ?? "0";
  // Shared array for the (overwhelmingly common) non-nested child — ONE
  // allocation per INSERT instead of one per child.
  const topOnly: readonly string[] = [insertLayer];
  const out: RenderableEntity[] = new Array(flattened.length);
  for (let i = 0; i < flattened.length; i++) {
    const child = flattened[i]!;
    const nested = ancestry.get(child.id);
    let chain = topOnly;
    if (nested !== undefined && nested.length > 0) {
      // A repeated layer adds nothing to the conjunction (a nested INSERT on
      // "0" resolves to the enclosing layer), so the chain stays deduplicated
      // and a non-nested-looking child keeps the shared single-entry array.
      const merged: string[] = [insertLayer];
      for (const layer of nested) {
        if (!merged.includes(layer)) merged.push(layer);
      }
      if (merged.length > 1) chain = merged;
    }
    out[i] = { ...child, renderParentLayers: chain };
  }
  return out;
}

/**
 * Cache-direct / re-lift INSERT lift: build the single `block-insert`
 * WRAPPER renderable whose `childRenderables` are the INSERT's flattened
 * children (see the module doc for the satellite contract).
 *
 * - `bounds` — union of the children's finite bounds, in the SAME
 *   offset-world frame every other lifted renderable uses (the flatten's
 *   builders apply `applyOffset` per leaf). When no child carries finite
 *   bounds the wrapper falls back to a degenerate box at the DISPLAYED
 *   insertion point, so the pick path's bbox distance still lands on the
 *   INSERT.
 * - `geometry.insertion / scale / rotation` — the DISPLAYED transform
 *   (`resolveBlockInsertTransform`: per-scale annotative override applied,
 *   same clamps as the flatten), insertion in offset-world so the OSNAP
 *   "insertion" candidate (`snap/osnapCandidates.ts`) agrees with the drawn
 *   geometry.
 * - `layer / colorHex / linetype / spaceId` — the INSERT's own (colour via
 *   the same `resolveColorHexFromEntity` chain the flatten threads as
 *   `blockColor`; linetype via `resolveInsertLinetypeTyped`, `ByLayer` when
 *   it resolves to Continuous — informational: the wrapper never reaches a
 *   dash material).
 * - `zOrder` — `ctx.zOrderIndex`, the DRAWORDER slot the lift caller
 *   assigned; children carry the same value from the flatten.
 *
 * Returns `null` ONLY when the flatten yields no children (annotatively
 * hidden INSERT / unknown block / empty block) — "nothing to draw", never a
 * missing arm. The annotative gate order is preserved: the caller
 * (`renderableFromDrawingEntity` → `typedAnnotativeHidden`) already returns
 * `null` for a hidden INSERT before reaching this function, and the flatten
 * re-checks the same one-truth gate.
 */
export function liftBlockInsertRenderable(args: {
  typedInsert: BlockInsertEntity;
  blocks: Record<string, BlockDefinition>;
  ctx: AdapterContext;
}): RenderableEntity | null {
  const { typedInsert: insert, blocks, ctx } = args;
  const transform = resolveBlockInsertTransform(insert, ctx);
  if (!transform) return null;
  const flattened = flattenBlockInsertForRender(insert, blocks, ctx);
  if (flattened.length === 0) return null;

  const childRenderables: RenderableEntity[] = new Array(flattened.length);
  let union: RenderableEntity["bounds"] | null = null;
  for (let i = 0; i < flattened.length; i++) {
    const child = flattened[i]!;
    // Shallow copy — never mutate the flatten's output; keep the child's
    // own `layer` (AutoCAD: only layer-"0" children follow the INSERT, and
    // the flatten already resolved that) and the `renderParentLayers` stamp
    // `flattenBlockInsertForRender` applied (module doc).
    childRenderables[i] = { ...child, renderParentId: insert.id };
    // Non-finite child bounds contribute nothing (a finite sum ⇔ every
    // corner finite — NaN / ±Infinity propagate through the addition).
    const b = child.bounds;
    if (!Number.isFinite(b.minX + b.minY + b.maxX + b.maxY)) continue;
    union = union ? unionBounds(union, child.bounds) : { ...child.bounds };
  }

  const insertion = applyOffset(transform.insertion);
  const bounds: RenderableEntity["bounds"] = union ?? {
    minX: insertion.x,
    minY: insertion.y,
    maxX: insertion.x,
    maxY: insertion.y,
  };
  const layer = insert.layer ?? "0";
  return {
    id: insert.id,
    spaceId: insert.spaceId,
    type: "Block",
    bounds,
    layer,
    colorHex: resolveColorHexFromEntity(
      insert,
      ctx.layerColors,
      ctx.blockColor ?? null,
      // Per the INSERT's OWN space, not the pass's active space: a layout pass
      // also carries the model-space INSERTs its viewports show, and those are
      // drawn on the dark VP background (`rendering/layoutForeground.ts`).
      entityForegroundHex(insert, ctx),
    ),
    lineweightWorld: 0,
    linetype:
      resolveInsertLinetypeTyped(insert.linetype, layer, ctx) ?? "ByLayer",
    zOrder: ctx.zOrderIndex,
    geometry: {
      kind: "block-insert",
      blockName: insert.blockName,
      insertion,
      scale: { ...transform.scale },
      rotation: transform.rotationDeg,
      childRenderables,
    },
  };
}
